import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../lib/api'

const REQUESTS_KEY = 'shift-replacement-requests'
const MY_OFFERS_KEY = 'shift-replacement-my-offers'

// ==================== Types ====================
export type ShiftReplacementStatus =
  | 'open'
  | 'filled'
  | 'exhausted'
  | 'cancelled'

export type OfferStatus = 'pending' | 'accepted' | 'declined' | 'expired'

export interface ShiftReplacementOffer {
  id: string
  request_id: string
  employee_id: string
  rank_index: number
  offered_xp: number
  status: OfferStatus
  sent_at: string | null
  respond_by: string | null
  responded_at: string | null
  employees?: { first_name: string; last_name: string } | null
}

export interface ShiftReplacementRequest {
  id: string
  date: string
  position_id: string
  location_id: string
  absent_employee_id: string
  base_xp: number
  xp_step: number
  xp_cap: number
  offer_ttl_min: number
  cooldown_min: number
  shift_deadline: string
  status: ShiftReplacementStatus
  filled_offer_id: string | null
  created_at: string
  positions?: { name: string } | null
  locations?: { name: string } | null
  absent_employee?: { first_name: string; last_name: string } | null
  offers: ShiftReplacementOffer[]
}

export interface CreateShiftReplacementRequest {
  absent_employee_id: string
  date: string
  /** Опционально: если не задано — бэкенд резолвит локацию из привязок сотрудника. */
  location_id?: string
  base_xp?: number
  xp_step?: number
  xp_cap?: number
  offer_ttl_min?: number
  cooldown_min?: number
}

// ==================== Queries ====================
export function useShiftReplacementRequests(status?: ShiftReplacementStatus) {
  return useQuery({
    queryKey: [REQUESTS_KEY, status ?? 'all'],
    queryFn: () =>
      apiClient.get('/api/v1/shift-replacements', {
        params: status ? { status } : undefined,
      }) as Promise<ShiftReplacementRequest[]>,
    refetchInterval: 30_000, // монитор живой — аукцион двигается cron-ом
  })
}

export function useShiftReplacementRequest(id: string | null) {
  return useQuery({
    queryKey: [REQUESTS_KEY, 'detail', id],
    queryFn: () =>
      apiClient.get(
        `/api/v1/shift-replacements/${id}`,
      ) as Promise<ShiftReplacementRequest>,
    enabled: !!id,
    refetchInterval: 15_000,
  })
}

export function useMyShiftReplacementOffers() {
  return useQuery({
    queryKey: [MY_OFFERS_KEY],
    queryFn: () =>
      apiClient.get('/api/v1/shift-replacements/my/offers') as Promise<
        ShiftReplacementOffer[]
      >,
  })
}

// ==================== Mutations ====================
export function useCreateShiftReplacement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateShiftReplacementRequest) =>
      apiClient.post('/api/v1/shift-replacements', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REQUESTS_KEY] })
    },
  })
}

export function useCancelShiftReplacement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`/api/v1/shift-replacements/${id}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REQUESTS_KEY] })
    },
  })
}

export function useAcceptShiftReplacementOffer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (offerId: string) =>
      apiClient.post(`/api/v1/shift-replacements/offers/${offerId}/accept`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MY_OFFERS_KEY] })
      queryClient.invalidateQueries({ queryKey: [REQUESTS_KEY] })
    },
  })
}

export function useDeclineShiftReplacementOffer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (offerId: string) =>
      apiClient.post(`/api/v1/shift-replacements/offers/${offerId}/decline`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MY_OFFERS_KEY] })
      queryClient.invalidateQueries({ queryKey: [REQUESTS_KEY] })
    },
  })
}
